import { useDroppable } from "@dnd-kit/core";
import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Download, Share2, Trash2 } from "lucide-react";
import { LIMITS, normalizeListKind, type ListKind } from "@checklist/shared";
import { ErrorBanner, fieldCls, inlineFieldCls, labelCls } from "../components/AuthShell.js";
import { ImportItemsModal } from "../components/ImportItemsModal.js";
import {
  DndContext,
  DragHandle,
  SortableList,
  SortableRow,
  arrayMove,
  closestCenter,
  parseSubgroupContainerId,
  subgroupContainerId,
  ungroupedContainerId,
  useDndSensors,
  type DragEndEvent,
  type UniqueIdentifier,
} from "../components/SortableList.js";
import { Button } from "../components/ui/Button.js";
import { useConfirm } from "../components/ui/ConfirmDialog.js";
import { CharCount } from "../components/ui/CharCount.js";
import { DescriptionHint } from "../components/ui/DescriptionHint.js";
import { ItemSortButtons } from "../components/ui/ItemSortButtons.js";
import { CopyItemsButton } from "../components/ui/CopyItemsButton.js";
import { RemoveRowButton } from "../components/ui/RemoveRowButton.js";
import { showErrorToast, showSuccessToast } from "../components/ui/Toast.js";
import { GuestNamePrompt } from "../components/GuestNamePrompt.js";
import { PdfExportDialog } from "../components/PdfExportDialog.js";
import { ResourceNotFound } from "../components/ResourceNotFound.js";
import { SharingPanel } from "../components/SharingPanel.js";
import {
  ApiError,
  createList,
  deleteList,
  fetchList,
  fetchTemplates,
  publishList,
  unpublishList,
  updateList,
  type ApiListItem,
  type ApiListSubgroup,
  resolveStoredShareRole,
} from "../lib/api.js";
import { useAuth } from "../context/AuthContext.js";
import { getGuestIdentity, setGuestName } from "../lib/shareSession.js";
import { humanizeApiError } from "../lib/apiErrors.js";
import { listDeleteMessage } from "../lib/createWarnings.js";
import {
  importResultMessage,
  selectImportItems,
} from "../lib/importParser.js";
import { sortItemsByText, type SortDir } from "../lib/sortItems.js";
import { listToPdfDoc } from "../lib/pdf/adapters.js";
import { useResourceLive } from "../hooks/useResourceLive.js";
import { DraftRestoreBar } from "../components/DraftRestoreBar.js";
import {
  GuardedLink,
  useDraftMirror,
  useRegisterUnsaved,
} from "../components/UnsavedChangesProvider.js";
import { clearDraft, draftKey, loadDraft } from "../lib/draftStorage.js";
import { diffCount } from "../lib/unsavedChanges.js";
import { formatPageTitle } from "../lib/pageTitle.js";

type DraftItem = ApiListItem;
type DraftSubgroup = ApiListSubgroup;
type ImportTarget = "ungrouped" | { subgroupId: string };
type LoadedList = {
  name: string;
  description?: string;
  tags: string[];
  kind: ListKind;
  items: ApiListItem[];
  subgroups: ApiListSubgroup[];
  isPublished: boolean;
  ownerId?: string;
  version?: number;
  memberCount?: number;
  activeShareCount?: number;
};

type ListDraft = {
  name: string;
  description: string;
  tagsText: string;
  kind: ListKind;
  items: DraftItem[];
  subgroups: DraftSubgroup[];
};

const EMPTY_LIST_DRAFT: ListDraft = {
  name: "",
  description: "",
  tagsText: "",
  kind: "packing",
  items: [],
  subgroups: [],
};

function isListDraft(value: unknown): value is ListDraft {
  return (
    !!value &&
    typeof value === "object" &&
    "name" in value &&
    "items" in value &&
    Array.isArray((value as ListDraft).items)
  );
}

function newId(): string {
  return crypto.randomUUID();
}

function totalItemCount(items: DraftItem[], subgroups: DraftSubgroup[]): number {
  return items.length + subgroups.reduce((n, sg) => n + sg.items.length, 0);
}

const ITEM_LIMIT_HINT = `Items can be up to ${LIMITS.itemTextMax} characters.`;

function itemTextKey(text: string): string {
  return text.trim().replace(/\s+/g, " ").toLowerCase();
}

function containerHasItem(rows: { text: string }[], text: string): boolean {
  const key = itemTextKey(text);
  return rows.some((it) => itemTextKey(it.text) === key);
}

function findItemContainer(
  id: UniqueIdentifier,
  items: DraftItem[],
  subgroups: DraftSubgroup[]
): string | null {
  const sid = String(id);
  if (items.some((it) => it.id === sid)) return ungroupedContainerId();
  for (const sg of subgroups) {
    if (sg.items.some((it) => it.id === sid)) return subgroupContainerId(sg.id);
  }
  if (sid === ungroupedContainerId()) return sid;
  if (parseSubgroupContainerId(sid) !== null) return sid;
  return null;
}

function kindToggleCls(active: boolean): string {
  return [
    "h-9 rounded-lg border px-3 text-sm font-semibold transition",
    active
      ? "border-brand-600 bg-brand-600 text-white"
      : "border-line bg-white text-ink hover:bg-slate-50",
  ].join(" ");
}

function DroppableRegion({
  id,
  children,
  className = "",
}: {
  id: string;
  children: ReactNode;
  className?: string;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id,
    data: { type: "container" },
  });
  return (
    <div
      ref={setNodeRef}
      className={[className, isOver ? "ring-2 ring-brand-500/30 rounded-lg" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  );
}

export function ListEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const confirm = useConfirm();
  const formId = useId();
  const sensors = useDndSensors();
  const { user } = useAuth();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tagsText, setTagsText] = useState("");
  const [kind, setKind] = useState<ListKind>("packing");
  const [items, setItems] = useState<DraftItem[]>([]);
  const [subgroups, setSubgroups] = useState<DraftSubgroup[]>([]);
  const [isPublished, setIsPublished] = useState(false);
  const [newItemText, setNewItemText] = useState("");
  const [newSubgroupItemText, setNewSubgroupItemText] = useState<Record<string, string>>(
    {}
  );
  const [newSubgroupName, setNewSubgroupName] = useState("");
  const [ungroupedDuplicate, setUngroupedDuplicate] = useState(false);
  const [subgroupDuplicate, setSubgroupDuplicate] = useState<Record<string, boolean>>({});

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    if (notFound) return;
    const title = name.trim() || (isNew ? "New list" : "List");
    document.title = formatPageTitle(title);
  }, [isNew, name, notFound]);
  useEffect(() => {
    if (!notFound) return;
    document.title = formatPageTitle("List not found");
  }, [notFound]);
  const [error, setError] = useState<string | null>(null);
  const [ownerId, setOwnerId] = useState("");
  const [version, setVersion] = useState(0);
  const [memberCount, setMemberCount] = useState(0);
  const [activeShareCount, setActiveShareCount] = useState(0);
  const [shareRole, setShareRole] = useState<"read" | "write" | null>(null);
  const [guestPromptOpen, setGuestPromptOpen] = useState(false);
  const savedDraftRef = useRef<string | null>(null);
  const currentDraftRef = useRef("");
  const [savedValue, setSavedValue] = useState<ListDraft>(EMPTY_LIST_DRAFT);
  const [pendingDraft, setPendingDraft] = useState<ListDraft | null>(null);

  const [importOpen, setImportOpen] = useState(false);
  const [importTarget, setImportTarget] = useState<ImportTarget | null>(null);

  const itemTotal = totalItemCount(items, subgroups);

  function draftSnapshot(value = {
    name,
    description,
    tagsText,
    kind,
    items,
    subgroups,
  }): string {
    return JSON.stringify(value);
  }
  currentDraftRef.current = draftSnapshot();

  const draftValue = useMemo<ListDraft>(
    () => ({ name, description, tagsText, kind, items, subgroups }),
    [name, description, tagsText, kind, items, subgroups]
  );
  const unsavedCount = diffCount(
    savedValue as unknown as Record<string, unknown>,
    draftValue as unknown as Record<string, unknown>
  );
  const mirrorKey = draftKey("list", !isNew && id ? id : "new");
  useRegisterUnsaved(`list:${!isNew && id ? id : "new"}`, unsavedCount);
  const draftMirror = useDraftMirror(mirrorKey, draftValue, unsavedCount > 0 && !loading);

  const restoreCheckedRef = useRef<string | null>(null);
  useEffect(() => {
    if (loading) return;
    if (restoreCheckedRef.current === mirrorKey) return;
    restoreCheckedRef.current = mirrorKey;
    const stored = loadDraft(mirrorKey);
    if (!isListDraft(stored)) return;
    if (
      diffCount(
        savedValue as unknown as Record<string, unknown>,
        stored as unknown as Record<string, unknown>
      ) === 0
    ) {
      clearDraft(mirrorKey);
      return;
    }
    setPendingDraft(stored);
  }, [loading, mirrorKey, savedValue]);

  function applyDraftValue(value: ListDraft) {
    setName(value.name);
    setDescription(value.description);
    setTagsText(value.tagsText);
    setKind(value.kind);
    setItems(value.items.map((it) => ({ ...it })));
    setSubgroups(
      value.subgroups.map((sg) => ({ ...sg, items: sg.items.map((it) => ({ ...it })) }))
    );
  }

  useEffect(() => {
    if (isNew) {
      setNotFound(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const list = await fetchList(id!);
        if (cancelled) return;
        applyListState(list);
        const role = await resolveStoredShareRole("list", id!);
        if (!cancelled && role) setShareRole(role);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403 && !user) {
          navigate(`/request-access/list/${id!}`, { replace: true });
          return;
        }
        if (err instanceof ApiError && err.status === 404) {
          setNotFound(true);
          return;
        }
        setError(humanizeApiError(err, "Failed to load list"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, navigate, user]);

  useEffect(() => {
    if (isNew || !id) return;
    function refetchIfClean() {
      if (savedDraftRef.current !== null && currentDraftRef.current !== savedDraftRef.current) {
        return;
      }
      void fetchList(id!).then(applyListState).catch(() => {
        // The initial loader owns visible errors; focus refetch is best-effort.
      });
    }
    function onVisibility() {
      if (document.visibilityState === "visible") refetchIfClean();
    }
    window.addEventListener("focus", refetchIfClean);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", refetchIfClean);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [id, isNew]);

  function parseTags(): string[] {
    return tagsText
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }

  function applyListState(list: LoadedList) {
    const savedObj: ListDraft = {
      name: list.name,
      description: list.description ?? "",
      tagsText: list.tags.join(", "),
      kind: normalizeListKind(list.kind),
      items: list.items.map((it) => ({ ...it })),
      subgroups: (list.subgroups ?? []).map((sg) => ({
        ...sg,
        items: sg.items.map((it) => ({ ...it })),
      })),
    };
    savedDraftRef.current = JSON.stringify(savedObj);
    setSavedValue(savedObj);
    setName(list.name);
    setDescription(list.description ?? "");
    setTagsText(list.tags.join(", "));
    setKind(normalizeListKind(list.kind));
    setItems(list.items.map((it) => ({ ...it })));
    setSubgroups(
      (list.subgroups ?? []).map((sg) => ({
        ...sg,
        items: sg.items.map((it) => ({ ...it })),
      }))
    );
    setIsPublished(list.isPublished);
    if (list.ownerId) setOwnerId(list.ownerId);
    if (list.version !== undefined) setVersion(list.version);
    setMemberCount(list.memberCount ?? 0);
    setActiveShareCount(list.activeShareCount ?? 0);
  }

  useResourceLive(
    "list",
    isNew ? undefined : id,
    { memberCount, activeShareCount },
    {
      onResource: (message) => {
        if (message.version <= version) return;
        if (savedDraftRef.current !== null && draftSnapshot() !== savedDraftRef.current) {
          const stale = "Someone else changed this — reload to see their changes.";
          showErrorToast(stale);
          setError(stale);
          return;
        }
        void fetchList(id!).then(applyListState).catch(() => {
          // The normal focus refetch remains the recovery path for a transient failure.
        });
      },
    }
  );

  const isOwner = Boolean(user && ownerId && user.sub === ownerId);
  const canWrite = isOwner || shareRole === "write";
  const readOnly = !isNew && !canWrite;

  function reportActionError(err: unknown, fallback: string) {
    showErrorToast(humanizeApiError(err, fallback));
    if (err instanceof ApiError && err.code === "STALE_VERSION") {
      setError("Someone else changed this — reload to see their changes.");
    }
  }

  function allItemTexts(): string[] {
    return [
      ...items.map((it) => it.text),
      ...subgroups.flatMap((sg) => sg.items.map((it) => it.text)),
    ];
  }

  function addUngroupedItem() {
    const text = newItemText.trim().replace(/\s+/g, " ");
    if (!text) return;
    if (containerHasItem(items, text)) {
      setUngroupedDuplicate(true);
      return;
    }
    setUngroupedDuplicate(false);
    if (itemTotal >= LIMITS.listItemsMax) {
      showErrorToast(`Lists can have at most ${LIMITS.listItemsMax} items.`);
      return;
    }
    setItems((prev) => [...prev, { id: newId(), text }]);
    setNewItemText("");
  }

  function addSubgroupItem(subgroupId: string) {
    const raw = newSubgroupItemText[subgroupId] ?? "";
    const text = raw.trim().replace(/\s+/g, " ");
    if (!text) return;
    const sg = subgroups.find((s) => s.id === subgroupId);
    if (sg && containerHasItem(sg.items, text)) {
      setSubgroupDuplicate((prev) => ({ ...prev, [subgroupId]: true }));
      return;
    }
    setSubgroupDuplicate((prev) => ({ ...prev, [subgroupId]: false }));
    if (itemTotal >= LIMITS.listItemsMax) {
      showErrorToast(`Lists can have at most ${LIMITS.listItemsMax} items.`);
      return;
    }
    setSubgroups((prev) =>
      prev.map((sg) =>
        sg.id === subgroupId
          ? { ...sg, items: [...sg.items, { id: newId(), text }] }
          : sg
      )
    );
    setNewSubgroupItemText((prev) => ({ ...prev, [subgroupId]: "" }));
  }

  function updateUngroupedItemText(itemId: string, text: string) {
    setItems((prev) => prev.map((it) => (it.id === itemId ? { ...it, text } : it)));
  }

  function updateSubgroupItemText(subgroupId: string, itemId: string, text: string) {
    setSubgroups((prev) =>
      prev.map((sg) =>
        sg.id === subgroupId
          ? {
              ...sg,
              items: sg.items.map((it) => (it.id === itemId ? { ...it, text } : it)),
            }
          : sg
      )
    );
  }

  function removeUngroupedItem(itemId: string) {
    setItems((prev) => prev.filter((it) => it.id !== itemId));
  }

  function removeSubgroupItem(subgroupId: string, itemId: string) {
    setSubgroups((prev) =>
      prev.map((sg) =>
        sg.id === subgroupId
          ? { ...sg, items: sg.items.filter((it) => it.id !== itemId) }
          : sg
      )
    );
  }

  function addSubgroup() {
    const nameNorm = newSubgroupName.trim().replace(/\s+/g, " ");
    if (!nameNorm) return;
    if (subgroups.length >= LIMITS.subgroupsMax) {
      showErrorToast(`Lists can have at most ${LIMITS.subgroupsMax} subgroups.`);
      return;
    }
    setSubgroups((prev) => [...prev, { id: newId(), name: nameNorm, items: [] }]);
    setNewSubgroupName("");
  }

  function renameSubgroup(subgroupId: string, name: string) {
    setSubgroups((prev) =>
      prev.map((sg) => (sg.id === subgroupId ? { ...sg, name } : sg))
    );
  }

  async function deleteSubgroup(subgroupId: string) {
    const sg = subgroups.find((s) => s.id === subgroupId);
    if (!sg) return;
    if (sg.items.length > 0) {
      const ok = await confirm({
        title: `Delete subgroup "${sg.name}"?`,
        message: `This will also delete its ${sg.items.length} item${
          sg.items.length === 1 ? "" : "s"
        }.`,
        confirmLabel: "Delete",
        destructive: true,
      });
      if (!ok) return;
    }
    setSubgroups((prev) => prev.filter((s) => s.id !== subgroupId));
  }

  function readContainerItems(
    containerId: string,
    curItems: DraftItem[],
    curSubgroups: DraftSubgroup[]
  ): DraftItem[] {
    if (containerId === ungroupedContainerId()) return curItems;
    const sgId = parseSubgroupContainerId(containerId);
    return curSubgroups.find((sg) => sg.id === sgId)?.items ?? [];
  }

  function writeContainerItems(
    containerId: string,
    next: DraftItem[],
    curItems: DraftItem[],
    curSubgroups: DraftSubgroup[]
  ): { items: DraftItem[]; subgroups: DraftSubgroup[] } {
    if (containerId === ungroupedContainerId()) {
      return { items: next, subgroups: curSubgroups };
    }
    const sgId = parseSubgroupContainerId(containerId);
    return {
      items: curItems,
      subgroups: curSubgroups.map((sg) =>
        sg.id === sgId ? { ...sg, items: next } : sg
      ),
    };
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;

    const activeType = active.data.current?.type as string | undefined;

    if (activeType === "subgroup") {
      const oldIndex = subgroups.findIndex((sg) => sg.id === active.id);
      const newIndex = subgroups.findIndex((sg) => sg.id === over.id);
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
      setSubgroups((prev) => arrayMove(prev, oldIndex, newIndex));
      return;
    }

    if (activeType !== "item") return;

    const activeContainer = findItemContainer(active.id, items, subgroups);
    let overContainer = findItemContainer(over.id, items, subgroups);
    if (!overContainer) {
      const overType = over.data.current?.type as string | undefined;
      if (overType === "container") {
        overContainer = String(over.id);
      } else if (overType === "subgroup") {
        overContainer = subgroupContainerId(String(over.id));
      }
    }
    if (!activeContainer || !overContainer) return;

    if (activeContainer === overContainer) {
      const containerItems = readContainerItems(activeContainer, items, subgroups);
      const oldIndex = containerItems.findIndex((it) => it.id === active.id);
      const newIndex = containerItems.findIndex((it) => it.id === over.id);
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
      const next = arrayMove(containerItems, oldIndex, newIndex);
      const written = writeContainerItems(activeContainer, next, items, subgroups);
      setItems(written.items);
      setSubgroups(written.subgroups);
      return;
    }

    // Cross-container move (single write so both sides update together)
    const sourceItems = [...readContainerItems(activeContainer, items, subgroups)];
    const destItems = [...readContainerItems(overContainer, items, subgroups)];
    const fromIndex = sourceItems.findIndex((it) => it.id === active.id);
    if (fromIndex < 0) return;
    const [moved] = sourceItems.splice(fromIndex, 1);
    if (!moved) return;

    const overIndex = destItems.findIndex((it) => it.id === over.id);
    if (overIndex >= 0) {
      destItems.splice(overIndex, 0, moved);
    } else {
      destItems.push(moved);
    }

    let nextItems = items;
    let nextSubgroups = subgroups;
    const afterSource = writeContainerItems(
      activeContainer,
      sourceItems,
      nextItems,
      nextSubgroups
    );
    nextItems = afterSource.items;
    nextSubgroups = afterSource.subgroups;
    const afterDest = writeContainerItems(
      overContainer,
      destItems,
      nextItems,
      nextSubgroups
    );
    setItems(afterDest.items);
    setSubgroups(afterDest.subgroups);
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      if (!user && shareRole === "write" && !getGuestIdentity().displayName) {
        setGuestPromptOpen(true);
        setSaving(false);
        return;
      }
      const body = {
        name,
        description: description.trim() || undefined,
        tags: parseTags(),
        kind,
        items: items.map((it) => ({
          id: it.id,
          text: it.text.trim(),
          ...(it.note ? { note: it.note } : {}),
        })),
        subgroups: subgroups.map((sg) => ({
          id: sg.id,
          name: sg.name.trim().replace(/\s+/g, " "),
          items: sg.items.map((it) => ({
            id: it.id,
            text: it.text.trim(),
            ...(it.note ? { note: it.note } : {}),
          })),
        })),
        ...(!isNew ? { version } : {}),
      };
      if (isNew) {
        const created = await createList(body);
        draftMirror.discard();
        setPendingDraft(null);
        showSuccessToast("List created.");
        navigate(`/lists/${created.id}`, { replace: true });
      } else {
        const updated = await updateList(id!, body);
        applyListState(updated);
        draftMirror.discard();
        setPendingDraft(null);
        showSuccessToast("List saved.");
      }
    } catch (err) {
      reportActionError(err, "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function onTogglePublish() {
    if (isNew) return;
    setError(null);

    if (!isPublished) {
      const ok = await confirm({
        title: "Publish this list?",
        message: "It will be visible to everyone in the community.",
        confirmLabel: "Publish",
      });
      if (!ok) return;
    }

    setPublishing(true);
    try {
      const updated = isPublished ? await unpublishList(id!) : await publishList(id!);
      setIsPublished(updated.isPublished);
      showSuccessToast(updated.isPublished ? "List published." : "List unpublished.");
    } catch (err) {
      reportActionError(err, "Publish update failed");
    } finally {
      setPublishing(false);
    }
  }

  async function onDelete() {
    if (isNew) return;
    let message = listDeleteMessage([]);
    try {
      const templates = await fetchTemplates();
      const names = templates
        .filter((tmpl) => (tmpl.listIds ?? []).includes(id!))
        .map((tmpl) => tmpl.name);
      message = listDeleteMessage(names);
    } catch {
      // A failed template lookup falls back to the plain delete warning.
    }
    const ok = await confirm({
      title: `Delete "${savedValue.name || "this list"}"?`,
      message,
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteList(id!);
      navigate("/lists", { replace: true });
    } catch (err) {
      reportActionError(err, "Failed to delete");
      setDeleting(false);
    }
  }

  function openImport(target: ImportTarget) {
    setImportTarget(target);
    setImportOpen(true);
  }

  function onImportConfirm(preview: string[]) {
    if (!importTarget) return;
    const remaining = LIMITS.listItemsMax - itemTotal;
    const { toAdd, skippedDuplicate, skippedLimit } = selectImportItems(
      preview,
      allItemTexts(),
      remaining
    );
    if (toAdd.length === 0) {
      showSuccessToast(importResultMessage(0, skippedDuplicate, skippedLimit));
      return;
    }
    const newItems: DraftItem[] = toAdd.map((text) => ({ id: newId(), text }));
    if (importTarget === "ungrouped") {
      setItems((prev) => [...prev, ...newItems]);
    } else {
      const { subgroupId } = importTarget;
      setSubgroups((prev) =>
        prev.map((sg) =>
          sg.id === subgroupId ? { ...sg, items: [...sg.items, ...newItems] } : sg
        )
      );
    }
    showSuccessToast(importResultMessage(toAdd.length, skippedDuplicate, skippedLimit));
  }

  function onSortContainer(target: ImportTarget, dir: SortDir) {
    if (target === "ungrouped") {
      setItems((prev) => sortItemsByText(prev, dir));
      return;
    }
    const { subgroupId } = target;
    setSubgroups((prev) =>
      prev.map((sg) =>
        sg.id === subgroupId ? { ...sg, items: sortItemsByText(sg.items, dir) } : sg
      )
    );
  }

  function renderItemRow(
    item: DraftItem,
    index: number,
    opts: {
      onTextChange: (text: string) => void;
      onRemove: () => void;
      containerId: string;
    }
  ) {
    return (
      <SortableRow
        key={item.id}
        id={item.id}
        data={{ type: "item", containerId: opts.containerId }}
      >
        {({ dragHandleProps, isDragging, setNodeRef, style }) => (
          <li
            ref={setNodeRef}
            style={style}
            className={[
              "flex items-center gap-1 rounded-lg border border-line bg-white px-2 py-1",
              isDragging ? "opacity-60 shadow-md" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <DragHandle {...dragHandleProps} />
            <div className="min-w-0 flex-1">
              <input
                className="w-full rounded-lg border border-transparent bg-transparent px-1 py-1.5 text-sm text-ink outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/20"
                value={item.text}
                maxLength={LIMITS.itemTextMax}
                onChange={(e) => opts.onTextChange(e.target.value)}
                aria-label={`Item ${index + 1}`}
              />
              {item.text.length >= LIMITS.itemTextMax ? (
                <p className="px-1 text-xs text-danger">{ITEM_LIMIT_HINT}</p>
              ) : null}
            </div>
            {!readOnly ? <RemoveRowButton onClick={opts.onRemove} /> : null}
          </li>
        )}
      </SortableRow>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center text-sm text-muted">Loading…</div>
    );
  }

  if (notFound) return <ResourceNotFound kind="list" />;

  const ungroupedIds = items.map((it) => it.id);
  const subgroupBlockIds = subgroups.map((sg) => sg.id);

  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8 sm:py-10">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <GuardedLink
            to="/lists"
            className="text-sm font-medium text-brand-700 no-underline hover:underline"
          >
            ← My Lists
          </GuardedLink>
          <h1 className="mt-2 text-2xl font-semibold text-ink">
            {isNew ? "New list" : "Edit list"}
          </h1>
        </div>
        {!isNew ? (
          <div className="ml-auto flex flex-wrap justify-end gap-2">
            {isOwner ? <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={publishing || saving}
              onClick={() => void onTogglePublish()}
            >
              {publishing
                ? "Updating…"
                : isPublished
                  ? "Unpublish"
                  : "Publish"}
            </Button> : null}
            <Button type="button" variant="secondary" size="icon" aria-label="Download PDF" title="Download PDF" onClick={() => setPdfOpen(true)}>
              <Download aria-hidden="true" size={20} />
            </Button>
            {isOwner ? (
              <Button type="button" variant="secondary" size="icon" aria-label="Share list" title="Share list" onClick={() => setShareOpen(true)}>
                <Share2 aria-hidden="true" size={20} />
              </Button>
            ) : null}
            {isOwner ? <Button
              type="button"
              variant="secondary"
              size="icon"
              className="text-red-700"
              aria-label="Delete list"
              title="Delete list"
              disabled={deleting || saving}
              onClick={() => void onDelete()}
            >
              <Trash2 aria-hidden="true" size={20} />
            </Button> : null}
          </div>
        ) : null}
      </div>

      {readOnly ? <p className="mb-4 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-muted">You have view-only access to this list.</p> : null}
      {!readOnly && pendingDraft ? (
        <div className="mb-5">
          <DraftRestoreBar
            onRestore={() => {
              applyDraftValue(pendingDraft);
              setPendingDraft(null);
            }}
            onDiscard={() => {
              clearDraft(mirrorKey);
              setPendingDraft(null);
            }}
          />
        </div>
      ) : null}
      <form id={formId} onSubmit={(e) => void onSave(e)} className="space-y-5">
        <fieldset disabled={readOnly} className="space-y-5 disabled:opacity-75">
        <ErrorBanner message={error} />
        {error?.includes("Someone else changed") ? <Button type="button" variant="secondary" size="sm" onClick={() => window.location.reload()}>Reload</Button> : null}

        <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm sm:p-6">
          <div>
            <label htmlFor="listName" className={labelCls}>
              Name
            </label>
            <input
              id="listName"
              className={fieldCls}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={LIMITS.nameMax}
              placeholder="e.g. Swimming"
            />
            <CharCount value={name} max={LIMITS.nameMax} />
            <DescriptionHint show={!description.trim()} />
          </div>
          <div className="mt-4">
            <p className={labelCls} id="listKindLabel">
              Kind
            </p>
            <div
              className="mt-1 flex flex-wrap gap-2"
              role="group"
              aria-labelledby="listKindLabel"
            >
              <button
                type="button"
                className={kindToggleCls(kind === "packing")}
                aria-pressed={kind === "packing"}
                onClick={() => setKind("packing")}
              >
                Packing
              </button>
              <button
                type="button"
                className={kindToggleCls(kind === "todo")}
                aria-pressed={kind === "todo"}
                onClick={() => setKind("todo")}
              >
                To-do
              </button>
            </div>
          </div>
          <div className="mt-4">
            <label htmlFor="listDesc" className={labelCls}>
              Description
            </label>
            <textarea
              id="listDesc"
              className={`${fieldCls} min-h-[4.5rem] resize-y`}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              placeholder="Optional notes about this list"
            />
            <CharCount value={description} max={500} />
          </div>
          <div className="mt-4">
            <label htmlFor="listTags" className={labelCls}>
              Tags
            </label>
            <input
              id="listTags"
              className={fieldCls}
              value={tagsText}
              onChange={(e) => setTagsText(e.target.value)}
              placeholder="comma-separated, e.g. camping, kids"
            />
            <p className="mt-1 text-xs text-muted">Tags are saved in lowercase.</p>
          </div>
          {!isNew && isPublished ? (
            <p className="mt-3 text-xs font-medium text-brand-700">
              Published — visible to everyone in the community.
            </p>
          ) : null}
        </div>

        <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-ink">
              Items{" "}
              <span className="font-normal text-muted">
                ({itemTotal}/{LIMITS.listItemsMax})
              </span>
            </h2>
            <Button type="button" variant="secondary" size="sm" onClick={() => openImport("ungrouped")}>
              Import paste
            </Button>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium text-ink">Ungrouped</p>
              <div className="flex flex-wrap gap-1.5">
                <CopyItemsButton items={items} />
                {!readOnly ? (
                  <ItemSortButtons
                    items={items}
                    onSort={(dir) => onSortContainer("ungrouped", dir)}
                  />
                ) : null}
              </div>
            </div>
            <DroppableRegion id={ungroupedContainerId()} className="mt-2 min-h-[2.5rem]">
              {items.length === 0 ? (
                <p className="text-sm text-muted">
                  No ungrouped items. Drag items here or add below.
                </p>
              ) : (
                <SortableList items={ungroupedIds} className="space-y-2">
                  <ul className="space-y-2">
                    {items.map((item, index) =>
                      renderItemRow(item, index, {
                        containerId: ungroupedContainerId(),
                        onTextChange: (text) => updateUngroupedItemText(item.id, text),
                        onRemove: () => removeUngroupedItem(item.id),
                      })
                    )}
                  </ul>
                </SortableList>
              )}
            </DroppableRegion>

            <div className="mt-4">
              <div className="flex gap-2">
                <input
                  className={inlineFieldCls}
                  value={newItemText}
                  maxLength={LIMITS.itemTextMax}
                  placeholder="Add an ungrouped item…"
                  aria-label="Add an ungrouped item…"
                  onChange={(e) => {
                    setNewItemText(e.target.value);
                    setUngroupedDuplicate(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addUngroupedItem();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="secondary"
                  className="shrink-0"
                  onClick={addUngroupedItem}
                >
                  Add
                </Button>
              </div>
              {newItemText.length >= LIMITS.itemTextMax ? (
                <p className="mt-1 text-xs text-danger">{ITEM_LIMIT_HINT}</p>
              ) : null}
              {ungroupedDuplicate ? (
                <p className="mt-1 text-xs text-danger">That item is already on the list.</p>
              ) : null}
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-ink">
                Subgroups{" "}
                <span className="font-normal text-muted">
                  ({subgroups.length}/{LIMITS.subgroupsMax})
                </span>
              </h3>
            </div>

            {subgroups.length === 0 ? (
              <p className="mt-2 text-sm text-muted">
                Optional named groups under this list (e.g. Toiletries, Electronics).
              </p>
            ) : (
              <SortableList items={subgroupBlockIds} className="mt-3 flex flex-col gap-2">
                {subgroups.map((sg) => {
                  const containerId = subgroupContainerId(sg.id);
                  const sgItemIds = sg.items.map((it) => it.id);
                  return (
                    <SortableRow
                      key={sg.id}
                      id={sg.id}
                      data={{ type: "subgroup" }}
                    >
                      {({ dragHandleProps, isDragging, setNodeRef, style }) => (
                        <div
                          ref={setNodeRef}
                          style={style}
                          className={[
                            "rounded-xl border border-line bg-white p-3 sm:p-4",
                            isDragging ? "opacity-60 shadow-md" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <DragHandle {...dragHandleProps} label="Drag to reorder subgroup" />
                            <input
                              className="min-w-0 flex-1 rounded-lg border border-line bg-transparent px-2 py-1.5 text-sm font-medium text-ink outline-none focus-visible:border-brand-500 focus-visible:ring-2 focus-visible:ring-brand-500/20"
                              value={sg.name}
                              maxLength={LIMITS.subgroupNameMax}
                              onChange={(e) => renameSubgroup(sg.id, e.target.value)}
                              aria-label="Subgroup name"
                            />
                            {!readOnly ? (
                              <ItemSortButtons
                                items={sg.items}
                                onSort={(dir) => onSortContainer({ subgroupId: sg.id }, dir)}
                              />
                            ) : null}
                            <CopyItemsButton items={sg.items} />
                            {!readOnly ? (
                              <RemoveRowButton onClick={() => void deleteSubgroup(sg.id)} />
                            ) : null}
                          </div>

                          <DroppableRegion id={containerId} className="mt-3 min-h-[2rem]">
                            {sg.items.length === 0 ? (
                              <p className="text-sm text-muted">No items in this subgroup.</p>
                            ) : (
                              <SortableList items={sgItemIds}>
                                <ul className="space-y-2">
                                  {sg.items.map((item, index) =>
                                    renderItemRow(item, index, {
                                      containerId,
                                      onTextChange: (text) =>
                                        updateSubgroupItemText(sg.id, item.id, text),
                                      onRemove: () => removeSubgroupItem(sg.id, item.id),
                                    })
                                  )}
                                </ul>
                              </SortableList>
                            )}
                          </DroppableRegion>

                          <div className="mt-3">
                            <div className="flex flex-wrap gap-2">
                            <input
                              className={inlineFieldCls}
                              value={newSubgroupItemText[sg.id] ?? ""}
                              maxLength={LIMITS.itemTextMax}
                              placeholder="Add item to subgroup…"
                              aria-label={`Add item to ${sg.name}`}
                              onChange={(e) => {
                                setNewSubgroupItemText((prev) => ({
                                  ...prev,
                                  [sg.id]: e.target.value,
                                }));
                                setSubgroupDuplicate((prev) => ({ ...prev, [sg.id]: false }));
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  addSubgroupItem(sg.id);
                                }
                              }}
                            />
                            <Button
                              type="button"
                              variant="secondary"
                              className="shrink-0"
                              onClick={() => addSubgroupItem(sg.id)}
                            >
                              Add
                            </Button>
                            <Button
                              type="button"
                              variant="secondary"
                              className="shrink-0"
                              onClick={() => openImport({ subgroupId: sg.id })}
                            >
                              Import paste
                            </Button>
                            </div>
                            {(newSubgroupItemText[sg.id] ?? "").length >= LIMITS.itemTextMax ? (
                              <p className="mt-1 text-xs text-danger">{ITEM_LIMIT_HINT}</p>
                            ) : null}
                            {subgroupDuplicate[sg.id] ? (
                              <p className="mt-1 text-xs text-danger">That item is already on the list.</p>
                            ) : null}
                          </div>
                        </div>
                      )}
                    </SortableRow>
                  );
                })}
              </SortableList>
            )}

            <div className="mt-4 flex gap-2">
              <input
                className={inlineFieldCls}
                value={newSubgroupName}
                maxLength={LIMITS.subgroupNameMax}
                placeholder="New subgroup name…"
                aria-label="New subgroup name…"
                onChange={(e) => setNewSubgroupName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addSubgroup();
                  }
                }}
              />
              <Button
                type="button"
                variant="secondary"
                className="shrink-0"
                onClick={addSubgroup}
              >
                Add subgroup
              </Button>
            </div>
          </DndContext>
        </div>

        <Button type="submit" disabled={saving}>
          {saving ? "Saving…" : isNew ? "Create list" : "Save changes"}
        </Button>
        </fieldset>
      </form>

      <PdfExportDialog
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        docTitle={name}
        supportsProgress={false}
        supportsAttribution={false}
        shareResource={id ? { kind: "list", id, isOwner } : undefined}
        buildDoc={(_options, qrUrl) =>
          listToPdfDoc({
            name,
            kind,
            description,
            items,
            subgroups,
            url: qrUrl,
          })
        }
      />
      {!isNew && id ? (
        <SharingPanel
          resourceKind="list"
          resourceId={id}
          resourceName={name}
          isOwner={isOwner}
          open={shareOpen}
          onClose={() => setShareOpen(false)}
        />
      ) : null}
      <GuestNamePrompt open={guestPromptOpen} onComplete={(guestName) => { setGuestName(guestName); setGuestPromptOpen(false); }} />

      <ImportItemsModal
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
          setImportTarget(null);
        }}
        onConfirm={onImportConfirm}
        confirmLabel="Append to list"
        remaining={LIMITS.listItemsMax - itemTotal}
        description={
          importTarget && importTarget !== "ungrouped"
            ? "Paste one item per line. Bullets, numbers, and the first cell of tab-separated lines are cleaned up. Items are added to this subgroup."
            : "Paste one item per line. Bullets, numbers, and the first cell of tab-separated lines are cleaned up. Items are added to the ungrouped list."
        }
      />
    </div>
  );
}
